import { type FunctionComponent, type ReactNode } from "react";

import { cn } from "@btnet/utilities-core";

import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";
import { BTCol } from "commonComponents/btWrappers/BTCol/BTCol";
import { BTIconDocuments, BTIconPaperClipOutlined } from "commonComponents/btWrappers/BTIcon";
import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import { BTTitle } from "commonComponents/btWrappers/BTTitle/BTTitle";
import { emptyStateIconSize } from "commonComponents/entity/emptyState/common/EmptyState.api.types";
import { BTFileUploadMode } from "commonComponents/entity/media/BTFileUpload/BTFileUpload.api.types";
import { ShowOnPortal } from "commonComponents/utilities/ShowOnPortal/ShowOnPortal";

interface IFileUploadAreaProps {
    uploadMode: BTFileUploadMode;
    onClickBrowseBTFiles?(): void;
    hasFileBeenUploaded: boolean;
    multiple?: boolean;
    /** Overrides the default heading shown in the drop area (applies to both empty and attached states). */
    title?: string;
    /** Optional sub-heading shown beneath the title. */
    description?: string;
    /**
     * Overrides the icon shown in the drop area for both the empty and attached states. Opt-in so
     * existing attachment screens keep their default documents/paperclip icons.
     */
    icon?: ReactNode;
    /**
     * Render the "Browse device" button as secondary instead of primary. Opt-in so existing attachment
     * screens keep their current primary-button styling.
     */
    secondaryBrowseDeviceButton?: boolean;
}

export const FileUploadArea: FunctionComponent<IFileUploadAreaProps> = ({
    uploadMode,
    onClickBrowseBTFiles,
    hasFileBeenUploaded,
    multiple,
    title,
    description,
    icon,
    secondaryBrowseDeviceButton,
}) => {
    const handleClickBrowseBTFiles = (event: React.MouseEvent) => {
        event.stopPropagation();
        onClickBrowseBTFiles?.();
    };

    const canBrowseBTFiles = uploadMode === BTFileUploadMode.Attachments && onClickBrowseBTFiles;

    return (
        <BTRow>
            <BTCol xs={24}>
                {hasFileBeenUploaded ? (
                    <>
                        <BTRow justify="center">
                            <BTCol>
                                {icon ? (
                                    <div className="margin-top-lg margin-bottom-md">{icon}</div>
                                ) : (
                                    <BTIconPaperClipOutlined
                                        size={56}
                                        className={cn(
                                            "AttachedFileIcon",
                                            "margin-top-lg",
                                            "margin-bottom-md",
                                        )}
                                        data-testid="AttachedFileIcon"
                                    />
                                )}
                            </BTCol>
                        </BTRow>
                        <BTRow justify="center">
                            <BTCol xs={16} lg={12} xl={8}>
                                <BTTitle level={2} className="text-center">
                                    {title ?? "Files attached"}
                                </BTTitle>
                            </BTCol>
                        </BTRow>
                        {description && (
                            <BTRow justify="center">
                                <BTCol xs={16} lg={12} xl={8}>
                                    <div className="text-center BTFileUpload-SecondaryText">
                                        {description}
                                    </div>
                                </BTCol>
                            </BTRow>
                        )}
                    </>
                ) : (
                    <>
                        <BTRow justify="center">
                            <BTCol>
                                {icon ? (
                                    <div className="margin-top-lg margin-bottom-md">{icon}</div>
                                ) : (
                                    <BTIconDocuments
                                        size={emptyStateIconSize}
                                        className={cn(
                                            "EmptyStateIcon",
                                            "margin-top-lg",
                                            "margin-bottom-md",
                                        )}
                                        data-testid="EmptyStateIcon"
                                    />
                                )}
                            </BTCol>
                        </BTRow>
                        <BTRow justify="center">
                            <BTCol xs={16} lg={12} xl={8}>
                                <BTTitle level={3} className="text-center">
                                    {title ?? "Drop files here to upload, or browse for files"}
                                </BTTitle>
                            </BTCol>
                        </BTRow>
                        {description && (
                            <BTRow justify="center">
                                <BTCol xs={16} lg={12} xl={8}>
                                    <div className="text-center BTFileUpload-SecondaryText">
                                        {description}
                                    </div>
                                </BTCol>
                            </BTRow>
                        )}
                    </>
                )}
                {(!hasFileBeenUploaded || multiple) && (
                    <BTRow justify="center" gutter={[8, 0]}>
                        <BTCol>
                            <BTButton
                                data-testid="browseComputerFiles"
                                className="margin-top-md margin-bottom-lg"
                                type={secondaryBrowseDeviceButton ? "secondary" : "primary"}
                            >
                                <ShowOnPortal builder render={() => "Browse device"} />
                                <ShowOnPortal btadmin subs owner render={() => "Browse"} />
                            </BTButton>
                        </BTCol>
                        {canBrowseBTFiles && (
                            <ShowOnPortal
                                builder
                                render={() => (
                                    <BTCol>
                                        <BTButton
                                            data-testid="browseBTFiles"
                                            className="margin-top-md margin-bottom-lg"
                                            type="secondary"
                                            onClick={handleClickBrowseBTFiles}
                                        >
                                            Browse Buildertrend
                                        </BTButton>
                                    </BTCol>
                                )}
                            />
                        )}
                    </BTRow>
                )}
            </BTCol>
        </BTRow>
    );
};
